import { Button } from "@/components/ui/button";
import { Plus, FileUp, Lock } from "lucide-react";
import { DashboardOverviewSection } from "./DashboardOverviewSection";
import { DashboardAccountsSection } from "./DashboardAccountsSection";
import { DashboardAgendaSection } from "./DashboardAgendaSection";
import type { MonthlyData, CategoryData, MonthlyComparison } from "@/hooks/use-dashboard-analytics";
import type { DragEndEvent } from "@dnd-kit/core";
import type { DueAlert } from "@/hooks/use-due-alerts";

interface DashboardDesktopLayoutProps {
  // Overview
  monthlyData: MonthlyData[];
  categoryData: CategoryData[];
  monthlyComparison: MonthlyComparison;
  analyticsLoading: boolean;
  // Accounts
  bankAccounts: any[];
  creditCards: any[];
  onAddAccount: () => void;
  onAddCreditCard?: () => void;
  onEditAccount: (account: any) => void;
  onAddTransaction: (accountId: string) => void;
  onViewInvoice: (accountId: string) => void;
  onImport: (accountId: string) => void;
  onAddExpense: (accountId: string) => void;
  onAccountDragEnd: (event: DragEndEvent) => void;
  onCreditCardDragEnd: (event: DragEndEvent) => void;
  formatCurrency: (value: number) => string;
  creditCardRefreshKey: number;
  // Actions
  onNewTransaction: () => void;
  onImportStatement: () => void;
  // Agenda
  onRefresh: () => void;
  onViewTransaction: (alert: DueAlert) => void;
  // Gating
  importLocked?: boolean;
  creditCardLocked?: boolean;
  allAccountsLocked?: boolean;
}

export function DashboardDesktopLayout(props: DashboardDesktopLayoutProps) {
  return (
    <div className="hidden md:block space-y-6">
      {/* Quick Actions */}
      <div className="flex gap-3">
        <Button onClick={props.onNewTransaction}>
          <Plus className="h-4 w-4 mr-2" />
          Nova Transacao
        </Button>
        <Button variant="outline" onClick={props.onImportStatement}>
          {props.importLocked ? (
            <Lock className="h-4 w-4 mr-2 text-muted-foreground" />
          ) : (
            <FileUp className="h-4 w-4 mr-2" />
          )}
          Importar Extrato
        </Button>
      </div>

      {/* Two-column layout */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: Charts */}
        <div className="lg:col-span-2">
          <DashboardOverviewSection
            monthlyData={props.monthlyData}
            categoryData={props.categoryData}
            monthlyComparison={props.monthlyComparison}
            isLoading={props.analyticsLoading}
          />
        </div>

        {/* Right: Agenda */}
        <div className="lg:col-span-1">
          <DashboardAgendaSection
            onRefresh={props.onRefresh}
            onViewTransaction={props.onViewTransaction}
          />
        </div>
      </div>

      {/* Accounts - full width */}
      <DashboardAccountsSection
        bankAccounts={props.bankAccounts}
        creditCards={props.creditCards}
        onAddAccount={props.onAddAccount}
        onAddCreditCard={props.onAddCreditCard}
        onEditAccount={props.onEditAccount}
        onAddTransaction={props.onAddTransaction}
        onViewInvoice={props.onViewInvoice}
        onImport={props.onImport}
        onAddExpense={props.onAddExpense}
        onAccountDragEnd={props.onAccountDragEnd}
        onCreditCardDragEnd={props.onCreditCardDragEnd}
        formatCurrency={props.formatCurrency}
        creditCardRefreshKey={props.creditCardRefreshKey}
        creditCardLocked={props.creditCardLocked}
        allAccountsLocked={props.allAccountsLocked}
      />
    </div>
  );
}
